import '../routes/publicPath';

import 'consumerweb-shim';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import React, { useMemo, Suspense } from 'react';
import { Route, Switch, Router, Redirect } from 'react-router-dom';
import history from '../routes/configureHistory';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import getStore from '../setup/store/configureStore';
import '../utility/csrf';
import Spinner from './spinner/Spinner';
import { setContainerMarginsBefore } from '../setup/utilsV3/headerUtils';
import { reactLazyPreload } from '../utility/lazyLoader';
import FallbackSpinner from './common/FallbackSpinner';
import '../../css/ppvx-buttonFactory.less';
import { ContextProvider as WorldReadyContextProvider } from '@paypalcorp/worldready-react';
import templateData from './../utility/getTemplateData';
import { WorldReadyProvider } from 'pp-react-worldready-v5';
import { IntlProvider } from 'pp-react-v5';
import { initializeWorldReady } from '../utility/worldReadyUtils';
import { createIntlProviderProps } from '../utility/intlProviderUtils';
import GlobalNavHeader from './globalnav/GlobalNavHeader';
import GlobalNavFooter from './globalnav/GlobalNavFooter';
import '../vendor/datadog';

initializeWorldReady();

!templateData.isFederated && setContainerMarginsBefore();
const store = getStore();

const PartnerTermsMerchantV3 = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "PartnerTermsMerchantV3" */ '../setup/componentsV3/partner/partnerTermsMerchant'
  )
);

const ButtonOverviewV3 = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "ButtonOverviewV3" */ '../setup/componentsV3/buttonManager/buttonOverview/buttonOverview'
  )
);

const DonationPagesManager = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "DonationPagesManager" */ '../setup/shared/donationPagesManager/donationPagesManager'
  )
);

const PartnerTermsCustomerV3 = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "PartnerTermsCustomerV3" */ '../setup/componentsV3/partner/partnerTermsCustomer'
  )
);

const ButtonFactoryWizardV3 = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "ButtonFactoryWizardV3" */ '../setup/componentsV3/ButtonFactoryWizardV3'
  )
);

const CampaignsWizard = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "CampaignsWizard" */ '../setup/campaigns/CampaignsWizard'
  )
);

const CampaignOverview = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "CampaignOverview" */ '../setup/campaigns/campaignOverview/CampaignOverview'
  )
);

const FullPageError = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "FullPageError" */ '../setup/shared/FullPageError'
  )
);

const PostStepupRedirect = reactLazyPreload(() =>
  import(
    /* webpackChunkName: "postStepupRedirect" */ './remoteModule/postStepupRedirect'
  )
);

const App = ({ isFederated }) => {
  const basePath = useMemo(() => (isFederated ? '/mep' : ''), [isFederated]);

  return (
    <div>
      <Router history={history}>
        <Suspense fallback={<FallbackSpinner />}>
          <Switch>
            <Route
              exact
              path={`${basePath}/donate/buttons/error`}
              component={FullPageError}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/partner`}
              component={() => <Redirect to={`${basePath}/donate/buttons`} />}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/partner/terms/merchant`}
              component={PartnerTermsMerchantV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/partner/terms/customer`}
              component={PartnerTermsCustomerV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/manage/:buttonId`}
              component={ButtonOverviewV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/manage`}
              component={DonationPagesManager}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/create`}
              component={ButtonFactoryWizardV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/login`}
              component={ButtonFactoryWizardV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/:buttonId/edit`}
              component={ButtonFactoryWizardV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/:buttonId/duplicate`}
              component={ButtonFactoryWizardV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons/:buttonId`}
              component={ButtonOverviewV3}
            />
            <Route
              exact
              path={`${basePath}/donate/buttons`}
              component={ButtonFactoryWizardV3}
            />
            <Route
              exact
              path={`${basePath}/donate/campaigns`}
              component={CampaignsWizard}
            />
            <Route
              exact
              path={`${basePath}/donate/campaigns/:campaignId/edit`}
              render={(props) => (
                <CampaignsWizard isEditing={true} {...props} />
              )}
            />
            <Route
              exact
              path={`${basePath}/donate/campaigns/:campaignId/duplicate`}
              render={(props) => (
                <CampaignsWizard isDuplicating={true} {...props} />
              )}
            />
            <Route
              exact
              path={`${basePath}/donate/campaigns/manage/:campaignId`}
              component={CampaignOverview}
            />
            <Route
              exact
              path={`/mep/donate/buttons/poststepup/:id`}
              component={PostStepupRedirect}
            />
            <Route
              exact
              path={`/mep/donate/campaigns/poststepup/:id`}
              component={PostStepupRedirect}
            />
          </Switch>
        </Suspense>
      </Router>
    </div>
  );
};
if (!templateData.isFederated) {
  const locale = `${templateData?.language || 'en'}-${templateData?.country || 'US'}`;
  const intlProviderProps = createIntlProviderProps(locale);

  ReactDOM.render(
    <Provider store={store}>
      <Router history={history}>
        <WorldReadyContextProvider locale={locale}>
          <WorldReadyProvider>
            <IntlProvider {...intlProviderProps}>
              <App />
            </IntlProvider>
          </WorldReadyProvider>
        </WorldReadyContextProvider>
      </Router>
    </Provider>,
    document.getElementById('react-buttons-container')
  );
}

if (document.getElementById('globalnav-header-container')) {
  ReactDOM.render(
    <Provider store={store}>
      <Router history={history}>
        <Suspense fallback={<div />}>
          <div>
            <Route path="/" component={GlobalNavHeader} />
          </div>
        </Suspense>
      </Router>
    </Provider>,
    document.getElementById('globalnav-header-container')
  );
}

if (document.getElementById('globalnav-footer-container')) {
  ReactDOM.render(
    <Provider store={store}>
      <Router history={history}>
        <Suspense fallback={<div />}>
          <div>
            <Route path="/" component={GlobalNavFooter} />
          </div>
        </Suspense>
      </Router>
    </Provider>,
    document.getElementById('globalnav-footer-container')
  );
}

ReactDOM.render(
  <Provider store={store}>
    <Router history={history}>
      <div>
        <Route path="/" component={Spinner} />
      </div>
    </Router>
  </Provider>,
  document.getElementById('button-factory-spinner')
);

export default App;
